import type { EdgeMetrics, MetricExplanation, MetricResult, MetricWarning, NodeMetrics } from '../contracts';
import { resolveMetricValue } from '../contracts';
import { evaluateNodeMetrics, manualNumber, resolveExitsAndConversion, type NodeMetricsResult } from '../node-metrics';
import { mergeIncomingFlows } from './merges';
import { resolveOutgoingSplit } from './splits';
import { cyclicNodeIds } from './cycles';
import { cycleRequiresManualAnchor } from '../warnings';

export interface PropagationNodeInput {
  id: string;
  metrics: NodeMetrics;
}

export interface PropagationEdgeInput {
  id: string;
  source: string;
  target: string;
  metrics: EdgeMetrics;
}

export interface PropagationGraphInput {
  nodes: readonly PropagationNodeInput[];
  edges: readonly PropagationEdgeInput[];
}

export interface EdgeMetricsResult {
  conversion: MetricResult;
  /** Runtime-only computed volume through this edge; never persisted. */
  flow: MetricResult;
}

export interface PropagationResult {
  nodeResults: Map<string, NodeMetricsResult>;
  edgeResults: Map<string, EdgeMetricsResult>;
  warnings: MetricWarning[];
}

/**
 * SPEC-008 §35-52/plan §15-20 — the pure propagation engine. Framework-free
 * (METRIC-REQ-012): takes a plain graph description, returns plain results.
 *
 * Callers are expected to pass only structurally-valid, "active" edges
 * (SPEC-006 already excludes dangling/self-loop/duplicate edges from the
 * live graph before this ever runs) — this engine does not re-validate
 * graph structure, only numeric propagation over it.
 */
export function propagateMetrics(graph: PropagationGraphInput): PropagationResult {
  const nodeIds = graph.nodes.map((node) => node.id);
  const outgoing = new Map<string, PropagationEdgeInput[]>();
  const incoming = new Map<string, PropagationEdgeInput[]>();
  for (const id of nodeIds) {
    outgoing.set(id, []);
    incoming.set(id, []);
  }
  for (const edge of graph.edges) {
    outgoing.get(edge.source)?.push(edge);
    incoming.get(edge.target)?.push(edge);
  }

  const cyclic = cyclicNodeIds(
    nodeIds,
    graph.edges.map((edge) => ({ from: edge.source, to: edge.target })),
  );

  const entries = new Map<string, number | null>();
  const exits = new Map<string, number | null>();
  const edgeConv = new Map<string, number | null>();

  const manualEntries = new Set<string>();
  const manualExits = new Set<string>();
  const manualEdgeConv = new Set<string>();

  for (const node of graph.nodes) {
    const manualE = manualNumber(node.metrics.entries);
    entries.set(node.id, manualE);
    if (manualE !== null) manualEntries.add(node.id);

    const manualX = manualNumber(node.metrics.exits);
    exits.set(node.id, manualX);
    if (manualX !== null) manualExits.add(node.id);
  }
  for (const edge of graph.edges) {
    const manualC = manualNumber(edge.metrics.conversion);
    edgeConv.set(edge.id, manualC);
    if (manualC !== null) manualEdgeConv.add(edge.id);
  }

  const maxPasses = nodeIds.length + graph.edges.length + 5;
  const snapshot = () =>
    JSON.stringify([[...entries], [...exits], [...edgeConv]]);
  let previous = snapshot();

  for (let pass = 0; pass < maxPasses; pass += 1) {
    // (a) exits from entries × manual node conversion, when exits isn't itself manual.
    for (const node of graph.nodes) {
      if (manualExits.has(node.id)) continue;
      const { exits: resolved } = resolveExitsAndConversion(entries.get(node.id) ?? null, node.metrics);
      exits.set(node.id, (resolved.effectiveValue as number | null) ?? null);
    }

    // (b) resolve outgoing splits from currently known edge conversions.
    for (const id of nodeIds) {
      const edgesOut = outgoing.get(id)!;
      if (edgesOut.length === 0) continue;
      const split = resolveOutgoingSplit(
        edgesOut.map((edge) => ({ edgeId: edge.id, conversion: edgeConv.get(edge.id) ?? null })),
      );
      for (const edge of edgesOut) {
        if (manualEdgeConv.has(edge.id)) continue;
        const resolution = split.results.get(edge.id);
        if (resolution && resolution.conversion !== null) {
          edgeConv.set(edge.id, resolution.conversion);
        }
      }
    }

    // (c) edge flow is derived fresh each pass in the result step, not
    // tracked as fixpoint state — only entries/exits/edgeConv need to
    // stabilize; flow is a pure function of the other two once stable.
    for (const node of graph.nodes) {
      if (manualEntries.has(node.id)) continue;
      const incomingFlows = incoming.get(node.id)!.map((edge) => {
        const sourceExits = exits.get(edge.source) ?? null;
        const conv = edgeConv.get(edge.id) ?? null;
        return sourceExits !== null && conv !== null ? sourceExits * conv : null;
      });
      const merged = mergeIncomingFlows(incomingFlows);
      entries.set(node.id, merged.value);
    }

    const current = snapshot();
    if (current === previous) break;
    previous = current;
  }

  // --- Build final results from the stabilized fixpoint state ---

  const warnings: MetricWarning[] = [];
  const nodeResults = new Map<string, NodeMetricsResult>();
  const edgeResults = new Map<string, EdgeMetricsResult>();

  for (const node of graph.nodes) {
    const entriesValue = entries.get(node.id) ?? null;
    const result = evaluateNodeMetrics(node.metrics, {
      entries: entriesValue,
      entriesOrigin: manualEntries.has(node.id) ? 'manual' : entriesValue !== null ? 'calculated' : null,
    });

    // SPEC-008 §48/METRIC-REQ-017 — re-derive the merge's own partial flag
    // (discarded by the plain number handed to evaluateNodeMetrics) so a
    // partial sum is never reported as if it were a complete total.
    if (!manualEntries.has(node.id)) {
      const incomingFlows = incoming.get(node.id)!.map((edge) => {
        const sourceExits = exits.get(edge.source) ?? null;
        const conv = edgeConv.get(edge.id) ?? null;
        return sourceExits !== null && conv !== null ? sourceExits * conv : null;
      });
      const merge = mergeIncomingFlows(incomingFlows, node.id);
      if (merge.partial) {
        result.entries.warnings = [...result.entries.warnings, ...merge.warnings];
      }
    }

    // SPEC-008 §50/METRIC-REQ-018 — a node inside an unresolved cycle never
    // gets its entries invented; it just carries an explicit warning
    // pointing at the need for a manual anchor.
    if (cyclic.has(node.id) && result.entries.status === 'unknown' && !manualEntries.has(node.id)) {
      result.entries.warnings = [...result.entries.warnings, cycleRequiresManualAnchor(undefined, [node.id])];
    }

    nodeResults.set(node.id, result);
    for (const key of ['entries', 'exits', 'conversion', 'cost', 'revenue', 'cpl', 'cac', 'averageTicket'] as const) {
      warnings.push(...result[key].warnings);
    }
  }

  for (const id of nodeIds) {
    const edgesOut = outgoing.get(id)!;
    if (edgesOut.length === 0) continue;
    const split = resolveOutgoingSplit(
      edgesOut.map((edge) => ({ edgeId: edge.id, conversion: manualEdgeConv.has(edge.id) ? edgeConv.get(edge.id)! : null })),
    );
    warnings.push(...split.warnings);
  }

  for (const edge of graph.edges) {
    const convValue = edgeConv.get(edge.id) ?? null;
    const isManual = manualEdgeConv.has(edge.id);
    let convExplanation: MetricExplanation | undefined;
    if (!isManual && convValue !== null) {
      const edgesOut = outgoing.get(edge.source)!;
      if (edgesOut.length === 1) {
        convExplanation = { metric: 'edgeConversion', formula: 'single_outgoing_edge_default', inputs: {}, result: convValue };
      } else {
        convExplanation = {
          metric: 'edgeConversion',
          formula: 'split_remainder',
          inputs: {},
          result: convValue,
        };
      }
    }
    const conversionResult = resolveMetricValue(edge.metrics.conversion, isManual ? null : convValue, [], convExplanation);

    const sourceExits = exits.get(edge.source) ?? null;
    const flowValue = sourceExits !== null && convValue !== null ? sourceExits * convValue : null;
    const flowResult: MetricResult =
      flowValue === null
        ? { status: 'unknown', effectiveValue: null, calculatedCandidate: null, origin: null, warnings: [] }
        : {
            status: 'known',
            effectiveValue: flowValue,
            calculatedCandidate: flowValue,
            origin: 'calculated',
            warnings: [],
            explanation: { metric: 'edgeFlow', formula: 'source.exits * edge.conversion', inputs: { sourceExits, conversion: convValue }, result: flowValue },
          };

    edgeResults.set(edge.id, { conversion: conversionResult, flow: flowResult });
  }

  return { nodeResults, edgeResults, warnings: dedupeWarnings(warnings) };
}

function dedupeWarnings(warnings: readonly MetricWarning[]): MetricWarning[] {
  const seen = new Set<string>();
  const result: MetricWarning[] = [];
  for (const warning of warnings) {
    const key = `${warning.code}:${(warning.refs ?? []).join(',')}`;
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(warning);
  }
  return result;
}

